import { useEffect, useState } from "react";
import {
  getSetupSprint, putSetupSprint, getSetupTickets,
  postSetupTickets, deleteSetupTicket,
  getSetupResponsaveis, putTicketResponsavel,
} from "../api/setup.api";
import { getSprintHistorico, getSprintDados } from "../api/sprint.api";
import { getGtdTickets } from "../api/gtd.api";
import GtdQuickAssign from "../components/GtdQuickAssign";
import { getParam } from "../utils/parametros";

/* ── Helpers ── */

// formato YYMMWW: 26 = ano, 05 = mês, 03 = semana do mês
function advanceGrupo(code, direction) {
  const n  = parseInt(code, 10) || 0;
  let yy   = Math.floor(n / 10000);
  let mm   = Math.floor((n % 10000) / 100);
  let ww   = n % 100;
  ww += direction;
  if (ww > 4) { ww = 1; mm += 1; }
  if (ww < 1) { ww = 4; mm -= 1; }
  if (mm > 12) { mm = 1; yy += 1; }
  if (mm < 1)  { mm = 12; yy -= 1; }
  return `${String(yy).padStart(2,"0")}${String(mm).padStart(2,"0")}${String(ww).padStart(2,"0")}`;
}

function advanceSprint(form, direction) {
  const days = direction * 7;

  // Parse sem conversão de fuso: usa componentes de data diretamente
  function shiftDate(str) {
    if (!str) return str;
    const [datePart, timePart = "00:00:00"] = str.slice(0, 19).split(" ");
    const [y, m, d] = datePart.split("-").map(Number);
    const dt = new Date(y, m - 1, d); // construtor local, sem UTC
    dt.setDate(dt.getDate() + days);
    const ny  = dt.getFullYear();
    const nm  = String(dt.getMonth() + 1).padStart(2, "0");
    const nd  = String(dt.getDate()).padStart(2, "0");
    return `${ny}-${nm}-${nd} ${timePart}`;
  }

  return {
    ...form,
    versao:        String((parseInt(form.versao, 10) || 0) + direction),
    grupo_entrega: advanceGrupo(form.grupo_entrega, direction),
    sprint:        advanceGrupo(form.sprint,        direction),
    data_inicial:  shiftDate(form.data_inicial),
    data_final:    shiftDate(form.data_final),
  };
}

const parseIds = (text) =>
  text
    .split(/[\n,;\s]+/)
    .map(s => parseInt(s.trim(), 10))
    .filter(n => !isNaN(n) && n > 0);

const STATUS_COLOR = {
  "Novo":                       "#a78bfa",
  "Rejeitado":                  "#f87171",
  "Parado":                     "#fb923c",
  "Aguardando Distribuição":    "#60a5fa",
  "Aguardando Desenvolvimento": "#38bdf8",
  "Em curso":                   "#fbbf24",
  "Em Desenvolvimento":         "#fbbf24",
  "Aguardando Testes":          "#4ade80",
  "Ajuste Especificação":       "#4ade80",
  "Feedback":                   "#4ade80",
  "Em Teste":                   "#4ade80",
  "Aguardando Versão":          "#4ade80",
  "Fechado":                    "#4ade80",
  "Resolvido":                  "#4ade80",
};
const STATUS_ORDER = [
  "Novo", "Rejeitado", "Parado",
  "Aguardando Distribuição", "Aguardando Desenvolvimento", "Em curso", "Em Desenvolvimento",
  "Aguardando Testes", "Ajuste Especificação", "Feedback",
  "Em Teste", "Aguardando Versão", "Fechado", "Resolvido",
];
const sc = (s) => STATUS_COLOR[s] ?? "#94a3b8";
const statusRank = (s) => { const i = STATUS_ORDER.indexOf(s); return i === -1 ? 99 : i; };

export default function Setup() {
  const [sprint,    setSprint]    = useState(null);
  const [tickets,   setTickets]   = useState([]);
  const [gtdMap,    setGtdMap]    = useState({});
  const [responsaveis, setResponsaveis] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [historico, setHistorico] = useState([]);

  /* ── Formulário: adicionar tickets ── */
  const [textarea,  setTextarea]  = useState("");
  const [tipo,      setTipo]      = useState(1);
  const [adding,    setAdding]    = useState(false);
  const [results,   setResults]   = useState(null);

  /* ── Filtros da lista de tickets ── */
  const [filterTipo,   setFilterTipo]   = useState(null); // null = todos, 1 = T1, 2 = T2
  const [filterDesc,   setFilterDesc]   = useState("");
  const [filterStatus, setFilterStatus] = useState(null); // null = todos

  /* ── Grupos colapsados na lista de tickets ── */
  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const toggleGroup = (name) => setCollapsedGroups(prev => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  /* ── Formulário: editar sprint ── */
  const [editSprint, setEditSprint] = useState(false);
  const [sprintForm, setSprintForm] = useState({});
  const [savingSprint, setSavingSprint] = useState(false);
  const [sprintMsg,    setSprintMsg]    = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [s, t, h, dadosResp, resp] = await Promise.all([
        getSetupSprint(),
        getSetupTickets(),
        getSprintHistorico("programado"),
        getSprintDados().catch(() => null),
        getSetupResponsaveis().catch(() => []),
      ]);
      setSprint(s);
      setResponsaveis(Array.isArray(resp) ? resp : []);

      // Monta mapa id_ticket → {desenvolvedor, versao} a partir de /sprint/dados
      const dadosLista = Array.isArray(dadosResp) ? dadosResp
        : Array.isArray(dadosResp?.dados) ? dadosResp.dados
        : [];
      const dadosMap = new Map(dadosLista.map(d => [Number(d.id), d]));

      const ticketsEnriquecidos = (Array.isArray(t) ? t : []).map(tk => {
        const extra = dadosMap.get(Number(tk.id_ticket));
        return {
          ...tk,
          desenvolvedor: tk.desenvolvedor || extra?.desenvolvedor || null,
        };
      });

      setTickets(ticketsEnriquecidos);
      setHistorico(Array.isArray(h) ? h : []);
      const form = {};
      Object.keys(s || {}).forEach(k => { form[k] = s[k] != null ? String(s[k]) : ""; });
      setSprintForm(form);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }

    getGtdTickets()
      .then(data => {
        const map = {};
        (data.tickets || []).forEach(t => { map[t.id_ticket] = t; });
        setGtdMap(map);
      })
      .catch(() => {});
  }

  useEffect(() => { load(); }, []);

  async function handleAdd() {
    const ids = parseIds(textarea);
    if (!ids.length) return;
    setAdding(true);
    setResults(null);
    try {
      const data = await postSetupTickets(ids, tipo);
      setResults(data);
      setTextarea("");
      await load();
    } catch (e) {
      setResults({ error: e?.response?.data?.error || "Erro ao adicionar tickets." });
    } finally {
      setAdding(false);
    }
  }

  async function handleDelete(id) {
    try {
      await deleteSetupTicket(id);
      setTickets(prev => prev.filter(t => t.id !== id));
    } catch (e) {
      console.error(e);
    }
  }

  async function handleResponsavelChange(id, idTicket, novoResponsavel) {
    const anterior = tickets.find(t => t.id === id)?.responsavel || "";
    setTickets(prev => prev.map(t => t.id === id ? { ...t, responsavel: novoResponsavel } : t));
    try {
      await putTicketResponsavel(idTicket, novoResponsavel);
    } catch (e) {
      console.error(e);
      setTickets(prev => prev.map(t => t.id === id ? { ...t, responsavel: anterior } : t));
    }
  }

  async function handleSaveSprint() {
    setSavingSprint(true);
    setSprintMsg(null);
    try {
      const data = await putSetupSprint(sprintForm);
      setSprint(data.sprint ?? sprintForm);
      setEditSprint(false);
      setSprintMsg({ ok: true, text: "Sprint atualizada com sucesso." });
    } catch (e) {
      setSprintMsg({ ok: false, text: e?.response?.data?.error || "Erro ao salvar." });
    } finally {
      setSavingSprint(false);
    }
  }

  /* ── Agrupamento por status (com filtros) ── */
  const ticketsFiltrados = tickets
    .filter(t => filterTipo == null || t.programacao_tipo === filterTipo)
    .filter(t => !filterDesc.trim() || (t.subject || "").toLowerCase().includes(filterDesc.toLowerCase()))
    .filter(t => !filterStatus || (t.status || "Sem Status") === filterStatus);

  const uniqueStatuses = [...new Set(tickets.map(t => t.status || "Sem Status"))]
    .sort((a, b) => statusRank(a) - statusRank(b));

  const byStatus = ticketsFiltrados.reduce((acc, t) => {
    const s = t.status || "Sem Status";
    (acc[s] = acc[s] || []).push(t);
    return acc;
  }, {});
  const statusGroups = Object.keys(byStatus)
    .sort((a, b) => statusRank(a) - statusRank(b));

  return (
    <div style={page}>

      {/* ── Header ── */}
      <header style={header}>
        <div>
          <h1 style={h1}>Setup da Programação</h1>
          {sprint && (
            <span style={sprintBadge}>
              Grupo de Entrega: <strong>{sprint.grupo_entrega}</strong>
              &nbsp;·&nbsp;{sprint.data_inicial} → {sprint.data_final}
            </span>
          )}
        </div>
        <button
          onClick={() => { setEditSprint(e => !e); setSprintMsg(null); }}
          style={btnOutline}
        >
          {editSprint ? "Cancelar" : "⚙ Editar Sprint"}
        </button>
      </header>

      {/* ── Editar parâmetros da sprint ── */}
      {editSprint && (
        <div style={glassCard}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={sectionTitle}>Parâmetros da Sprint (gp_parametro_relatorio)</div>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                style={navSprintBtn}
                onClick={() => setSprintForm(prev => advanceSprint(prev, -1))}
                title="Recuar 7 dias — versão, grupo, sprint e datas"
              >‹ Anterior</button>
              <button
                type="button"
                style={navSprintBtn}
                onClick={() => setSprintForm(prev => advanceSprint(prev, +1))}
                title="Avançar 7 dias — versão, grupo, sprint e datas"
              >Próxima ›</button>
            </div>
          </div>
          <div style={formGrid}>
            {[
              { key: "versao",        label: "Versão",           type: "text"           },
              { key: "grupo_entrega", label: "Grupo de Entrega",  type: "text"           },
              { key: "sprint",        label: "Sprint",            type: "text"           },
              { key: "data_inicial",  label: "Data Inicial",      type: "datetime-local" },
              { key: "data_final",    label: "Data Final",        type: "datetime-local" },
            ].map(({ key, label, type }) => {
              const isDate = type === "datetime-local";
              const raw = sprintForm[key] ?? "";
              const displayVal = isDate ? raw.replace(" ", "T").slice(0, 16) : raw;
              return (
                <div key={key} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <label style={fieldLabel}>{label}</label>
                  <input
                    type={type}
                    value={displayVal}
                    onChange={e => {
                      const val = isDate
                        ? e.target.value.replace("T", " ") + ":00"
                        : e.target.value;
                      setSprintForm(prev => ({ ...prev, [key]: val }));
                    }}
                    style={{ ...inputStyle, colorScheme: "dark" }}
                  />
                </div>
              );
            })}
          </div>
          {sprintMsg && (
            <div style={{ ...alertBox, borderColor: sprintMsg.ok ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)", background: sprintMsg.ok ? "rgba(74,222,128,0.07)" : "rgba(248,113,113,0.07)", color: sprintMsg.ok ? "#4ade80" : "#f87171" }}>
              {sprintMsg.text}
            </div>
          )}
          <button
            onClick={handleSaveSprint}
            disabled={savingSprint}
            style={{ ...btnPrimary, opacity: savingSprint ? 0.7 : 1, alignSelf: "flex-start" }}
          >
            {savingSprint ? "Salvando..." : "Salvar Sprint"}
          </button>
        </div>
      )}

      <div style={mainRow}>

        {/* ── Coluna esquerda: capacidade + adicionar tickets ── */}
        <div style={leftCol}>
          <CapacidadePanel sprint={sprint} tickets={tickets} historico={historico} />

          <div style={{ ...glassCard, flex: 1, overflow: "hidden" }}>
            <div style={sectionTitle}>Adicionar Tickets à Programação</div>

            {/* Tipo */}
            <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
              <TipoBtn active={tipo === 1} color="#22d3ee" onClick={() => setTipo(1)}>◎ Principal (T1)</TipoBtn>
              <TipoBtn active={tipo === 2} color="#a78bfa" onClick={() => setTipo(2)}>◈ Secundário (T2)</TipoBtn>
            </div>

            {/* Textarea */}
            <label style={fieldLabel}>IDs dos tickets (um por linha, colados do Excel)</label>
            <textarea
              value={textarea}
              onChange={e => setTextarea(e.target.value)}
              placeholder={"211216\n212830\n211982"}
              rows={5}
              style={{ ...textareaStyle, flex: 1, minHeight: 80, resize: "none" }}
            />

            <button
              onClick={handleAdd}
              disabled={adding || !textarea.trim()}
              style={{ ...btnPrimary, opacity: (adding || !textarea.trim()) ? 0.6 : 1, cursor: (adding || !textarea.trim()) ? "not-allowed" : "pointer" }}
            >
              {adding ? "Adicionando..." : `Adicionar como ${tipo === 1 ? "Principal (T1)" : "Secundário (T2)"}`}
            </button>
          </div>

          {/* Resultados */}
          {results && (
            <div style={glassCard}>
              <div style={sectionTitle}>Resultado</div>
              {results.error ? (
                <div style={{ ...alertBox, borderColor: "rgba(248,113,113,0.3)", background: "rgba(248,113,113,0.07)", color: "#f87171" }}>
                  {results.error}
                </div>
              ) : (
                <>
                  <div style={{ display: "flex", gap: 12, marginBottom: 10 }}>
                    <span style={{ ...pill, background: "rgba(74,222,128,0.12)", border: "1px solid rgba(74,222,128,0.3)", color: "#4ade80" }}>✓ {results.inserted ?? 0} adicionados</span>
                    <span style={{ ...pill, background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.3)", color: "#fbbf24" }}>⚠ {results.skipped ?? 0} já existiam</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {(results.results ?? []).map((r, i) => (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, padding: "4px 0", borderBottom: "1px solid rgba(96,165,250,0.05)" }}>
                        <span style={{ color: r.status === "inserted" ? "#4ade80" : r.status === "exists" ? "#fbbf24" : "#f87171", fontWeight: 700 }}>
                          {r.status === "inserted" ? "✓" : r.status === "exists" ? "⚠" : "✗"}
                        </span>
                        <span style={{ color: "#94a3b8", fontWeight: 700 }}>#{r.id}</span>
                        <span style={{ color: "#475569" }}>
                          {r.status === "inserted" ? "adicionado" : r.status === "exists" ? "já existe" : r.message ?? "erro"}
                        </span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* ── Coluna direita: tickets na programação (altura total) ── */}
        <div style={{ ...glassCard, flex: 1, minHeight: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <div style={sectionTitle}>Tickets na Programação — {sprint?.grupo_entrega ?? "..."}</div>
            <span style={{ fontSize: 11, color: "#475569" }}>{ticketsFiltrados.length}/{tickets.length}</span>
          </div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10, flexShrink: 0 }}>
            {[
              { label: "Todos",         value: null, color: "#60a5fa", count: tickets.length },
              { label: "◎ Principal",   value: 1,    color: "#22d3ee", count: tickets.filter(t => t.programacao_tipo === 1).length },
              { label: "◈ Secundário",  value: 2,    color: "#a78bfa", count: tickets.filter(t => t.programacao_tipo === 2).length },
            ].map(({ label, value, color, count }) => {
              const active = filterTipo === value;
              return (
                <button
                  key={String(value)}
                  onClick={() => setFilterTipo(value)}
                  style={{
                    flex: 1,
                    padding: "6px 8px",
                    borderRadius: 7,
                    border: `1px solid ${active ? color : "rgba(96,165,250,0.1)"}`,
                    background: active ? `${color}15` : "rgba(255,255,255,0.02)",
                    color: active ? color : "#475569",
                    fontSize: 10,
                    fontWeight: 700,
                    cursor: "pointer",
                    transition: "all 0.15s",
                    whiteSpace: "nowrap",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <span style={{ fontSize: 18, fontWeight: 800, color: active ? color : "#64748b", lineHeight: 1 }}>{count}</span>
                  <span>{label}</span>
                </button>
              );
            })}
          </div>

          {/* ── Filtro: descrição ── */}
          <input
            type="text"
            value={filterDesc}
            onChange={e => setFilterDesc(e.target.value)}
            placeholder="Buscar por descrição..."
            style={{ ...inputStyle, fontSize: 12, padding: "6px 10px", flexShrink: 0 }}
          />

          {/* ── Filtro: situação ── */}
          {uniqueStatuses.length > 0 && (
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", flexShrink: 0 }}>
              {[{ label: "Todos", value: null }, ...uniqueStatuses.map(s => ({ label: s, value: s }))].map(({ label, value }) => {
                const active = filterStatus === value;
                const color = value ? sc(value) : "#60a5fa";
                return (
                  <button
                    key={String(value)}
                    onClick={() => setFilterStatus(value)}
                    style={{
                      padding: "3px 9px", borderRadius: 20, fontSize: 10, fontWeight: 700, cursor: "pointer",
                      border: `1px solid ${active ? color : "rgba(96,165,250,0.12)"}`,
                      background: active ? `${color}18` : "rgba(255,255,255,0.02)",
                      color: active ? color : "#475569",
                      transition: "all 0.15s", whiteSpace: "nowrap",
                    }}
                  >
                    {value && <span style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: color, marginRight: 4, verticalAlign: "middle" }} />}
                    {label}
                    {value && <span style={{ marginLeft: 4, color: active ? color : "#334155" }}>{tickets.filter(t => (t.status || "Sem Status") === value).length}</span>}
                  </button>
                );
              })}
            </div>
          )}

          {loading ? (
            <div style={{ color: "#475569", fontSize: 13 }}>Carregando...</div>
          ) : tickets.length === 0 ? (
            <div style={{ color: "#334155", fontSize: 13, textAlign: "center", paddingTop: 40 }}>
              Nenhum ticket na programação desta sprint.
            </div>
          ) : (
            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
              {statusGroups.map(status => {
                const items = byStatus[status];
                const color = sc(status);
                const collapsed = collapsedGroups.has(status);
                return (
                  <div key={status}>
                    {/* ── Cabeçalho do grupo — clicável ── */}
                    <button
                      onClick={() => toggleGroup(status)}
                      style={{
                        width: "100%", display: "flex", alignItems: "center", gap: 7,
                        padding: "6px 8px", borderRadius: 7, cursor: "pointer",
                        background: `${color}0d`, border: `1px solid ${color}22`,
                        marginBottom: collapsed ? 0 : 4, transition: "background 0.15s",
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = `${color}18`; }}
                      onMouseLeave={e => { e.currentTarget.style.background = `${color}0d`; }}
                    >
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, boxShadow: `0 0 5px ${color}`, flexShrink: 0 }} />
                      <span style={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: 0.7, flex: 1, textAlign: "left" }}>
                        {status}
                      </span>
                      <span style={{ fontSize: 13, fontWeight: 800, color, minWidth: 20, textAlign: "right" }}>{items.length}</span>
                      <span style={{ fontSize: 10, color: `${color}70`, transition: "transform 0.15s", display: "inline-block", transform: collapsed ? "rotate(-90deg)" : "rotate(0deg)" }}>▾</span>
                    </button>

                    {/* ── Lista de tickets ── */}
                    {!collapsed && items.map(t => {
                      const tipoColor = t.programacao_tipo === 1 ? "#22d3ee" : t.programacao_tipo === 2 ? "#a78bfa" : "#475569";
                      const tipoLabel = t.programacao_tipo === 1 ? "T1" : t.programacao_tipo === 2 ? "T2" : null;
                      return (
                        <div
                          key={t.id}
                          onClick={() => window.open(`https://redmine.lexsis.com.br/issues/${t.id_ticket}`, "_blank")}
                          style={{ ...ticketRow, marginBottom: 3, cursor: "pointer" }}
                          onMouseEnter={e => { e.currentTarget.style.background = "rgba(96,165,250,0.06)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.15)"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.02)"; e.currentTarget.style.borderColor = "rgba(96,165,250,0.06)"; }}
                        >
                          <span style={{ fontSize: 11, fontWeight: 700, color: tipoColor, flexShrink: 0 }}>#{t.id_ticket}</span>
                          {tipoLabel && (
                            <span style={{ fontSize: 9, fontWeight: 800, color: tipoColor, background: `${tipoColor}15`, border: `1px solid ${tipoColor}30`, borderRadius: 4, padding: "1px 4px", flexShrink: 0 }}>{tipoLabel}</span>
                          )}
                          {/* Badge dev */}
                          <span style={{
                            fontSize: 9, borderRadius: 4, padding: "1px 5px", fontWeight: 600, flexShrink: 0,
                            color: t.desenvolvedor ? "#22d3ee" : "#334155",
                            background: t.desenvolvedor ? "rgba(34,211,238,0.08)" : "rgba(255,255,255,0.02)",
                            border: `1px solid ${t.desenvolvedor ? "rgba(34,211,238,0.18)" : "rgba(255,255,255,0.05)"}`,
                          }}>
                            👤 {t.desenvolvedor ? t.desenvolvedor.split(" ")[0] : "—"}
                          </span>
                          {/* Badge versão (fixed_version do Redmine) */}
                          {(() => { const ver = t.fixed_version || t.versao_nome; return (
                            <span style={{
                              fontSize: 9, borderRadius: 4, padding: "1px 5px", fontWeight: 600, flexShrink: 0,
                              color: ver ? "#a78bfa" : "#334155",
                              background: ver ? "rgba(167,139,250,0.08)" : "rgba(255,255,255,0.02)",
                              border: `1px solid ${ver ? "rgba(167,139,250,0.18)" : "rgba(255,255,255,0.05)"}`,
                            }}>📌 {ver || "—"}</span>
                          ); })()}
                          {/* Combo Responsável (custom field Redmine) */}
                          <select
                            value={t.responsavel || ""}
                            onClick={e => e.stopPropagation()}
                            onChange={e => handleResponsavelChange(t.id, t.id_ticket, e.target.value)}
                            title="Responsável"
                            style={{
                              fontSize: 9, borderRadius: 4, padding: "1px 4px", fontWeight: 600, flexShrink: 0,
                              maxWidth: 100, cursor: "pointer",
                              color: t.responsavel ? "#facc15" : "#334155",
                              background: t.responsavel ? "rgba(250,204,21,0.08)" : "rgba(255,255,255,0.02)",
                              border: `1px solid ${t.responsavel ? "rgba(250,204,21,0.18)" : "rgba(255,255,255,0.05)"}`,
                            }}
                          >
                            <option value="">🧭 —</option>
                            {responsaveis.map(nome => (
                              <option key={nome} value={nome}>{nome}</option>
                            ))}
                          </select>
                          <span style={{ fontSize: 11, color: "#94a3b8", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
                            {t.subject || <span style={{ color: "#334155", fontStyle: "italic" }}>sem título</span>}
                          </span>
                          <span onClick={e => e.stopPropagation()} style={{ flexShrink: 0 }}>
                            <GtdQuickAssign
                              idTicket={t.id_ticket}
                              categoriaAtual={gtdMap[t.id_ticket]?.categoria}
                              observacaoAtual={gtdMap[t.id_ticket]?.observacao}
                              onChanged={registro => setGtdMap(prev => {
                                const next = { ...prev };
                                if (registro) next[t.id_ticket] = registro;
                                else delete next[t.id_ticket];
                                return next;
                              })}
                            />
                          </span>
                          <button
                            onClick={e => { e.stopPropagation(); handleDelete(t.id); }}
                            title="Remover da programação"
                            style={removeBtn}
                            onMouseEnter={e => { e.currentTarget.style.color = "#f87171"; e.currentTarget.style.borderColor = "rgba(248,113,113,0.3)"; }}
                            onMouseLeave={e => { e.currentTarget.style.color = "#475569"; e.currentTarget.style.borderColor = "rgba(255,255,255,0.06)"; }}
                          >✕</button>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function TipoBtn({ active, color, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1,
        padding: "8px 12px",
        borderRadius: 9,
        border: `1px solid ${active ? color : "rgba(96,165,250,0.12)"}`,
        background: active ? `${color}15` : "rgba(255,255,255,0.03)",
        color: active ? color : "#475569",
        fontSize: 12,
        fontWeight: 700,
        cursor: "pointer",
        transition: "all 0.15s",
      }}
    >
      {children}
    </button>
  );
}

/* ── Styles ── */
const page = { display: "flex", flexDirection: "column", gap: 16, height: "calc(100vh - 56px)", overflow: "hidden" };
const header = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 };
const h1 = { fontSize: 22, fontWeight: 800, color: "#f1f5f9", letterSpacing: -0.5, marginBottom: 4 };
const sprintBadge = { fontSize: 11, color: "#475569" };

const glassCard = {
  background: "rgba(11,22,42,0.72)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(96,165,250,0.1)",
  borderRadius: 14,
  padding: "18px 20px",
  display: "flex",
  flexDirection: "column",
  gap: 12,
  overflow: "hidden",
};

const mainRow = { display: "flex", gap: 14, flex: 1, minHeight: 0, overflow: "hidden" };
const leftCol = { display: "flex", flexDirection: "column", gap: 14, width: 340, flexShrink: 0, minHeight: 0 };

const sectionTitle = { fontSize: 11, fontWeight: 700, color: "#cbd5e1", textTransform: "uppercase", letterSpacing: 1.2, flexShrink: 0 };

const formGrid = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 };

const fieldLabel = { fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6 };

const inputStyle = {
  padding: "8px 12px",
  background: "rgba(255,255,255,0.04)",
  border: "1px solid rgba(96,165,250,0.18)",
  borderRadius: 8,
  color: "#e2e8f0",
  fontSize: 13,
  outline: "none",
  fontFamily: "Inter, sans-serif",
};

const textareaStyle = {
  ...inputStyle,
  resize: "vertical",
  fontFamily: "monospace",
  fontSize: 13,
  lineHeight: 1.6,
  minHeight: 120,
};

const alertBox = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid",
  fontSize: 12,
};

const btnPrimary = {
  padding: "10px 20px",
  borderRadius: 9,
  border: "1px solid rgba(96,165,250,0.4)",
  background: "rgba(96,165,250,0.15)",
  color: "#60a5fa",
  fontSize: 13,
  fontWeight: 700,
  cursor: "pointer",
  transition: "opacity 0.15s",
};

const navSprintBtn = {
  padding: "5px 12px", borderRadius: 8, fontSize: 11, fontWeight: 700,
  border: "1px solid rgba(96,165,250,0.2)", background: "rgba(96,165,250,0.06)",
  color: "#60a5fa", cursor: "pointer", transition: "all 0.15s",
};

const btnOutline = {
  padding: "7px 14px",
  borderRadius: 8,
  border: "1px solid rgba(96,165,250,0.2)",
  background: "rgba(96,165,250,0.06)",
  color: "#60a5fa",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

const pill = { padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700 };

const ticketRow = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 8px",
  borderRadius: 7,
  background: "rgba(255,255,255,0.02)",
  border: "1px solid rgba(96,165,250,0.06)",
  marginBottom: 4,
};

const removeBtn = {
  width: 22,
  height: 22,
  borderRadius: 5,
  border: "1px solid rgba(255,255,255,0.06)",
  background: "transparent",
  color: "#475569",
  fontSize: 11,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "color 0.15s, border-color 0.15s",
  flexShrink: 0,
};

/* ─── Painel de Capacidade ───────────────────────────────────────── */

function diasUteis(inicio, fim) {
  let count = 0;
  const cur = new Date(inicio); cur.setHours(0, 0, 0, 0);
  const end = new Date(fim);    end.setHours(0, 0, 0, 0);
  while (cur <= end) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function CapacidadePanel({ sprint, tickets, historico }) {
  if (!sprint || tickets.length === 0) return null;

  const hoje  = new Date();
  const inicio = sprint.data_inicial ? new Date(sprint.data_inicial.replace(" ", "T")) : null;
  const fim    = sprint.data_final   ? new Date(sprint.data_final.replace(" ", "T"))   : null;

  const totalDias    = inicio && fim ? diasUteis(inicio, fim)           : null;
  const diasRestantes = fim          ? Math.max(0, diasUteis(hoje, fim)) : null;

  // historico buscado com ?tipo=programado — saida.geral já contém só programados
  function normItem(h) {
    const saida = h.saida?.geral || h.indicadores?.saida?.geral || h.saida || h;
    const total = saida.total ?? saida.total_geral ?? 0;
    const pct   = saida.percentual ?? 0;
    let fechado = saida.fechado ?? saida.fechados ?? saida.closed ?? -1;
    if (fechado < 0) {
      fechado = pct > 0 && total > 0 ? Math.round(pct * total / 100) : 0;
    }
    return { total, fechado };
  }

  const qtdSprints    = getParam("sprints_historico") || 4;
  const historicoNorm = historico.length > 0
    ? historico.map(normItem).filter(i => i.fechado > 0).slice(-qtdSprints)
    : [];

  // Velocidade histórica = média de tickets FECHADOS por sprint (últimas N sprints)
  const velocidadeMedia = historicoNorm.length > 0
    ? Math.round(historicoNorm.reduce((s, i) => s + i.fechado, 0) / historicoNorm.length)
    : null;

  const FECHADOS = new Set([
    "Fechado", "Resolvido", "Em Teste", "Feedback",
    "Aguardando Testes", "Aguardando Versão", "Ajuste Especificação",
  ]);

  const t1All      = tickets.filter(t => t.programacao_tipo === 1);
  const t2All      = tickets.filter(t => t.programacao_tipo === 2);
  const t1         = t1All.length;
  const t2         = t2All.length;
  const t1Fechados = t1All.filter(t => FECHADOS.has(t.status)).length;
  const t2Fechados = t2All.filter(t => FECHADOS.has(t.status)).length;
  const t1Pct      = t1 > 0 ? Math.round(t1Fechados / t1 * 100) : 0;
  const t2Pct      = t2 > 0 ? Math.round(t2Fechados / t2 * 100) : 0;
  const pctColor   = (p) => p >= 70 ? "#4ade80" : p >= 40 ? "#fbbf24" : "#f87171";

  const totalAtual  = t1 + t2; // só programados — emergências não entram na capacidade

  const utilizacao = velocidadeMedia ? Math.round((totalAtual / velocidadeMedia) * 100) : null;
  const capColor   = utilizacao == null ? "#60a5fa"
                   : utilizacao > 120   ? "#f87171"
                   : utilizacao > 90    ? "#fbbf24"
                   : "#4ade80";

  const ticketsPorDia = diasRestantes > 0
    ? (tickets.filter(t => !["Fechado","Resolvido","Em Teste","Feedback","Aguardando Testes","Aguardando Versão"].includes(t.status)).length / diasRestantes).toFixed(1)
    : null;

  // Previsão baseada na capacidade produtiva histórica
  const ticketsAbertos     = totalAtual - t1Fechados - t2Fechados;
  const velocidadeDiaria   = (totalDias > 0 && velocidadeMedia) ? velocidadeMedia / totalDias : null;
  const diasParaConcluir   = (velocidadeDiaria > 0 && ticketsAbertos > 0)
    ? Math.ceil(ticketsAbertos / velocidadeDiaria) : 0;
  const diasAlem           = diasRestantes != null ? diasParaConcluir - diasRestantes : null;
  const forecastColor      = diasAlem == null ? "#94a3b8"
                           : diasAlem <= 0    ? "#4ade80"
                           : diasAlem <= 2    ? "#fbbf24"
                           : "#f87171";

  return (
    <div style={{
      ...glassCard,
      padding: "10px 12px",
      borderColor: `${capColor}30`,
      flexShrink: 0,
    }}>
      <div style={{ ...sectionTitle, marginBottom: 8 }}>Planejamento de Capacidade</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>

      {/* Bloco: carga atual + progresso T1/T2 */}
      <div style={capBlock}>
        <div style={capLabel}>Tickets na sprint</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: capColor, lineHeight: 1, marginBottom: 4 }}>{totalAtual}</div>
        <div style={{ fontSize: 9, color: "#334155", marginBottom: 4 }}>
          programados (T1+T2)
          {tickets.length > totalAtual && (
            <span style={{ color: "#475569" }}> · +{tickets.length - totalAtual} emergências</span>
          )}
        </div>

        {/* T1 */}
        {t1 > 0 && (
          <div style={{ marginBottom: 4 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ fontSize: 9, color: "#22d3ee", fontWeight: 700 }}>◎ T1 — {t1}</span>
              <span style={{ fontSize: 9, fontWeight: 800, color: pctColor(t1Pct) }}>{t1Pct}%</span>
            </div>
            <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden", marginBottom: 2 }}>
              <div style={{ height: "100%", width: `${t1Pct}%`, background: pctColor(t1Pct), borderRadius: 3, transition: "width 0.5s" }} />
            </div>
            <div style={{ fontSize: 9, color: "#334155" }}>
              <span style={{ color: "#4ade80" }}>{t1Fechados}</span> fechados · <span style={{ color: "#fbbf24" }}>{t1 - t1Fechados}</span> abertos
            </div>
          </div>
        )}

        {/* T2 */}
        {t2 > 0 && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
              <span style={{ fontSize: 9, color: "#a78bfa", fontWeight: 700 }}>◈ T2 — {t2}</span>
              <span style={{ fontSize: 9, fontWeight: 800, color: pctColor(t2Pct) }}>{t2Pct}%</span>
            </div>
            <div style={{ height: 3, background: "rgba(255,255,255,0.06)", borderRadius: 3, overflow: "hidden", marginBottom: 2 }}>
              <div style={{ height: "100%", width: `${t2Pct}%`, background: pctColor(t2Pct), borderRadius: 3, transition: "width 0.5s" }} />
            </div>
            <div style={{ fontSize: 9, color: "#334155" }}>
              <span style={{ color: "#4ade80" }}>{t2Fechados}</span> fechados · <span style={{ color: "#fbbf24" }}>{t2 - t2Fechados}</span> abertos
            </div>
          </div>
        )}
      </div>

      {/* Bloco: velocidade histórica */}
      <div style={capBlock}>
        <div style={capLabel}>Média histórica</div>
        <div style={{ fontSize: 22, fontWeight: 900, color: "#60a5fa", lineHeight: 1 }}>
          {velocidadeMedia ?? "—"}
        </div>
        <div style={{ fontSize: 9, color: "#475569", marginTop: 3 }}>entregues/sprint (últ. {historicoNorm.length})</div>
      </div>

      {/* Bloco: utilização (largura total) */}
      <div style={{ ...capBlock, gridColumn: "1 / -1" }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
          <div>
            <div style={capLabel}>Utilização da capacidade</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontSize: 20, fontWeight: 900, color: capColor, lineHeight: 1 }}>
                {utilizacao != null ? `${utilizacao}%` : "—"}
              </span>
              {utilizacao != null && (
                <span style={{ fontSize: 10, color: capColor, fontWeight: 700 }}>
                  {utilizacao > 120 ? "⚠ Sobrecarga" : utilizacao > 90 ? "! Atenção" : "✓ Normal"}
                </span>
              )}
            </div>
          </div>
          {velocidadeDiaria > 0 && ticketsAbertos > 0 && (
            <div style={{ marginLeft: "auto", textAlign: "right" }}>
              <div style={capLabel}>Previsão</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                <span style={{ fontSize: 16, fontWeight: 900, color: forecastColor, lineHeight: 1 }}>{diasParaConcluir}d</span>
                {diasAlem != null && (
                  <span style={{ fontSize: 9, color: forecastColor, fontWeight: 700 }}>
                    {diasAlem <= 0 ? `✓ ${Math.abs(diasAlem)}d folga` : `⚠ +${diasAlem}d`}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
        {velocidadeMedia && (
          <div style={{ height: 4, background: "rgba(255,255,255,0.05)", borderRadius: 4, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.min(100, utilizacao)}%`, background: capColor, borderRadius: 4, transition: "width 0.6s" }} />
          </div>
        )}
      </div>

      {/* Bloco: dias */}
      {totalDias !== null && (
        <div style={capBlock}>
          <div style={capLabel}>Prazo</div>
          <div style={{ fontSize: 20, fontWeight: 900, color: diasRestantes === 0 ? "#f87171" : "#94a3b8", lineHeight: 1 }}>
            {diasRestantes}
          </div>
          <div style={{ fontSize: 9, color: "#475569", marginTop: 3 }}>dias úteis restantes de {totalDias}</div>
          {ticketsPorDia && (
            <div style={{ fontSize: 9, color: "#fbbf24", marginTop: 2 }}>~{ticketsPorDia} tickets/dia</div>
          )}
        </div>
      )}

      </div>{/* fecha grid */}
    </div>
  );
}

const capBlock = {
  padding: "6px 8px",
  background: "rgba(255,255,255,0.02)",
  border: "1px solid rgba(96,165,250,0.07)",
  borderRadius: 8,
  display: "flex", flexDirection: "column",
};
const capLabel = {
  fontSize: 9, fontWeight: 700, color: "#475569",
  textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 4,
};
